// v2.19.38b — Fiche détaillée unifiée (claim ou POI OSM).
// Style "AYA SEARCH" : panneau latéral droit desktop, bottom-sheet mobile.
// Sections : header photo, titre, action bar, description, détails OSM, tags chips,
//            bouton revendication (commerce only), section avis sociale, "à proximité" stub.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "civ_user_token";
const PROFILE_KEY = "civ_user_profile";

const PIN_COLORS = {
  perso:     "#FFD700",
  pro:       "#00E5FF",
  monuments: "#A78BFA",
  commerces: "#10B981",
  officiels: "#3B82F6",
  tourism:   "#06B6D4",
  logements: "#F59E0B",
  services:  "#94A3B8",
  autre:     "#94A3B8",
};

const PIN_ICONS = {
  perso: "🏠", pro: "🏪",
  monuments: "🏛️", commerces: "🏪", officiels: "📋",
  tourism: "✨", logements: "🏨", services: "🔧", autre: "📍",
};

function _getUserProfile() {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || "null"); } catch { return null; }
}

function _buildAudioText(props) {
  const name = props.name || props.friendly_name || "Lieu sans nom";
  const cat = props.category_aya || props.category || props.type || "lieu";
  const addr = props.address_text || props.address_civ || props.commune || "Côte d'Ivoire";
  const hours = props.opening_hours ? ` Horaires : ${props.opening_hours}.` : "";
  return `Voici ${name}, ${cat} situé à ${addr}.${hours} Découverte AYA AI.`;
}

export default function PoiCardSheet({ feature, onClose }) {
  const audioRef = useRef(null);
  const [audioBase64, setAudioBase64] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimName, setClaimName] = useState("");
  const [claimProof, setClaimProof] = useState("");
  const [reviews, setReviews] = useState({ list: [], avg: null, count: 0 });
  const [reviewDraft, setReviewDraft] = useState({ rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const profile = _getUserProfile();
  const isCommerce = profile?.role === "commerce" || profile?.role === "owner";
  const props = feature?.properties || {};
  const isOsmPoi = props.source === "osm";
  const isClaim = props.source === "claim";
  const cat = props.category_aya || props.category || props.type || "autre";
  const color = PIN_COLORS[cat] || PIN_COLORS[props.type] || PIN_COLORS.autre;
  const icon = PIN_ICONS[cat] || PIN_ICONS[props.type] || PIN_ICONS.autre;
  const idKey = props.osm_id || props.claim_id || "unknown";

  // Charge les reviews si OSM POI
  useEffect(() => {
    if (!isOsmPoi || !idKey) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/v3/poi/${encodeURIComponent(idKey)}/reviews?limit=5`);
        const j = await r.json();
        if (j.ok) setReviews({ list: j.reviews || [], avg: j.average, count: j.count });
      } catch (_) {}
    })();
  }, [idKey, isOsmPoi]);

  const onListenAi = async () => {
    if (audioBase64) {
      // Toggle play/pause
      if (audioRef.current) {
        if (audioPlaying) { audioRef.current.pause(); setAudioPlaying(false); }
        else { audioRef.current.play(); setAudioPlaying(true); }
      }
      return;
    }
    setAudioLoading(true);
    try {
      const text = _buildAudioText(props);
      const r = await fetch(`${API}/api/v3/poi/audio`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, mode: "casual" }),
      });
      const j = await r.json();
      if (!j.ok) {
        toast.error(j.error === "tts_failed_or_placeholder_key" ? "Voix IA indisponible (clé ElevenLabs non configurée)" : "Erreur audio");
        return;
      }
      setAudioBase64(j.audio_base64);
      setTimeout(() => { audioRef.current?.play(); setAudioPlaying(true); }, 80);
    } catch (_) { toast.error("Erreur réseau audio"); }
    finally { setAudioLoading(false); }
  };

  const onShare = async () => {
    const txt = `📍 ${props.name || props.friendly_name} — découvert sur AYA AI`;
    try {
      if (navigator.share) await navigator.share({ title: txt, text: txt, url: window.location.href });
      else { await navigator.clipboard.writeText(`${txt}\n${window.location.href}`); toast.success("📋 Lien copié"); }
    } catch (_) {}
  };

  const onItineraire = () => {
    const [lng, lat] = feature?.geometry?.coordinates || [0, 0];
    window.open(`https://www.openstreetmap.org/directions?to=${lat}%2C${lng}`, "_blank", "noopener");
  };

  const onSubmitClaim = async () => {
    if (!claimName.trim()) { toast.error("Donne un nom à ta revendication"); return; }
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { toast.error("Connecte-toi d'abord"); return; }
    setSubmitting(true);
    try {
      const [lng, lat] = feature.geometry.coordinates;
      const r = await fetch(`${API}/api/v3/poi/${encodeURIComponent(idKey)}/claim-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          requested_name: claimName,
          proof_text: claimProof,
          lat, lng,
          address_text: props.address_text || props.address_civ,
        }),
      });
      const j = await r.json();
      if (!j.ok) { toast.error(j.error || "Erreur"); return; }
      toast.success(`✅ Demande envoyée (#${j.id.slice(-6)})`);
      setShowClaimForm(false);
      setClaimName(""); setClaimProof("");
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setSubmitting(false); }
  };

  const onSubmitReview = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { toast.error("Connecte-toi pour laisser un avis"); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/v3/poi/${encodeURIComponent(idKey)}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating: reviewDraft.rating, comment: reviewDraft.comment }),
      });
      const j = await r.json();
      if (!j.ok) { toast.error(j.error || "Erreur"); return; }
      toast.success("⭐ Avis ajouté");
      // Refetch
      const rr = await fetch(`${API}/api/v3/poi/${encodeURIComponent(idKey)}/reviews?limit=5`);
      const jj = await rr.json();
      if (jj.ok) setReviews({ list: jj.reviews || [], avg: jj.average, count: jj.count });
      setReviewDraft({ rating: 5, comment: "" });
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setSubmitting(false); }
  };

  if (!feature) return null;

  return (
    <div
      data-testid="poi-card-sheet"
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(420px, 100vw)",
        background: "linear-gradient(180deg, #0F1A2E 0%, #131E36 100%)",
        color: "#F4F1E8",
        boxShadow: "-12px 0 36px rgba(0,0,0,0.45)",
        overflowY: "auto",
        zIndex: 9999,
        animation: "poi-slide-in 240ms ease-out",
        borderLeft: "1px solid rgba(212,175,55,0.18)",
      }}
    >
      <style>{`
        @keyframes poi-slide-in { from { transform: translateX(100%); opacity: 0.6; } to { transform: translateX(0); opacity: 1; } }
      `}</style>

      {/* ─── Hero photo / placeholder gradient ─── */}
      <div style={{
        position: "relative",
        height: 180,
        background: props.photo_url
          ? `url(${props.photo_url}) center/cover`
          : `linear-gradient(135deg, ${color}, ${color}88, #0F1A2E)`,
      }}>
        <button
          data-testid="poi-close-btn"
          onClick={onClose}
          style={{
            position: "absolute", top: 12, right: 12,
            width: 36, height: 36, borderRadius: "50%",
            background: "rgba(15,26,46,0.75)", color: "#F4F1E8", border: "1px solid rgba(255,255,255,0.15)",
            cursor: "pointer", fontSize: 18, fontWeight: 700,
          }}
        >×</button>
        <div style={{
          position: "absolute", top: 12, left: 12,
          background: "rgba(15,26,46,0.85)", padding: "6px 12px", borderRadius: 999,
          fontSize: 11, fontWeight: 700, letterSpacing: 1, color, border: `1px solid ${color}55`,
        }}>
          {icon} {String(cat).toUpperCase()}
        </div>
        {!props.photo_url && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 72 }}>
            {icon}
          </div>
        )}
      </div>

      {/* ─── Titre + sous-titre + rating ─── */}
      <div style={{ padding: "16px 18px 4px" }}>
        <h2 data-testid="poi-name" style={{ margin: 0, fontSize: 20, color: "#F4F1E8" }}>
          {props.name || props.friendly_name || "Lieu sans nom"}
        </h2>
        <div style={{ fontSize: 12, color: "rgba(244,241,232,0.6)", marginTop: 4 }}>
          {props.sub_category || props.category || "—"} · {props.commune || props.address_text || "Côte d'Ivoire"}
        </div>
        {reviews.avg != null && (
          <div style={{ marginTop: 6, fontSize: 13 }}>
            <span style={{ color: "#FBBF24" }}>{"★".repeat(Math.round(reviews.avg))}</span>
            <span style={{ color: "rgba(244,241,232,0.6)" }}> ({reviews.avg.toFixed(1)} · {reviews.count} avis)</span>
          </div>
        )}
      </div>

      {/* ─── Action bar ─── */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6,
        padding: "12px 18px", borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <ActionBtn testid="poi-listen-btn" icon={audioLoading ? "⏳" : audioPlaying ? "⏸" : "🔊"} label={audioPlaying ? "Pause" : "Écouter"} onClick={onListenAi} />
        <ActionBtn testid="poi-route-btn" icon="🗺" label="Itinéraire" onClick={onItineraire} />
        <ActionBtn testid="poi-info-btn" icon="ℹ️" label="Infos" onClick={() => toast.info(`Tags : ${Object.keys(props.tags_raw || {}).slice(0, 6).join(', ') || 'aucun'}`)} />
        <ActionBtn testid="poi-share-btn" icon="📤" label="Partager" onClick={onShare} />
      </div>

      {/* Audio element hidden */}
      {audioBase64 && (
        <audio
          ref={audioRef}
          src={`data:audio/mpeg;base64,${audioBase64}`}
          onEnded={() => setAudioPlaying(false)}
          style={{ display: "none" }}
        />
      )}

      {/* ─── Description / Tags / Détails ─── */}
      <div style={{ padding: "14px 18px" }}>
        {props.opening_hours && (
          <DetailLine icon="🕒" label="Horaires" value={props.opening_hours} />
        )}
        {props.phone && (
          <DetailLine icon="📞" label="Téléphone" value={props.phone} />
        )}
        {props.website && (
          <DetailLine icon="🌐" label="Site" value={<a href={props.website} target="_blank" rel="noopener noreferrer" style={{ color: "#D4AF37" }}>{props.website.replace(/^https?:\/\//, '').slice(0, 30)}</a>} />
        )}
        {props.address_civ && (
          <DetailLine icon="📍" label="Adresse CIV" value={<code style={{ color: "#FBBF24", fontWeight: 700 }}>{props.address_civ}</code>} />
        )}
        {props.tags_raw && Object.keys(props.tags_raw).length > 0 && (
          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.entries(props.tags_raw).slice(0, 8).map(([k, v]) => (
              <span key={k} style={{
                fontSize: 10, padding: "3px 8px", borderRadius: 999,
                background: "rgba(255,255,255,0.06)", color: "rgba(244,241,232,0.75)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}>{k}={String(v).slice(0, 18)}</span>
            ))}
          </div>
        )}
      </div>

      {/* ─── Bouton Revendiquer (commerce only, OSM POI uniquement) ─── */}
      {isOsmPoi && isCommerce && (
        <div style={{ padding: "8px 18px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          {!showClaimForm ? (
            <button
              data-testid="poi-claim-btn"
              onClick={() => setShowClaimForm(true)}
              style={{
                width: "100%", padding: "10px 14px", borderRadius: 10,
                background: "linear-gradient(135deg, #D4AF37, #A07C20)",
                color: "#0F1A2E", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
            >📍 Revendiquer cette adresse</button>
          ) : (
            <div data-testid="poi-claim-form" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input
                placeholder="Nom de mon commerce (ex: Maison Awa)"
                value={claimName}
                onChange={(e) => setClaimName(e.target.value)}
                style={inputStyle}
              />
              <textarea
                placeholder="Preuve / justification (gérant·e, facture, contrat…)"
                value={claimProof}
                onChange={(e) => setClaimProof(e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setShowClaimForm(false)} style={btnGhost}>Annuler</button>
                <button data-testid="poi-claim-submit" onClick={onSubmitClaim} disabled={submitting} style={btnPrimary}>
                  {submitting ? "Envoi…" : "Envoyer"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Section avis sociale (OSM POI uniquement) ─── */}
      {isOsmPoi && (
        <div data-testid="poi-reviews" style={{ padding: "12px 18px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#F4F1E8" }}>⭐ Avis de la communauté</h3>
          {reviews.list.length === 0 && (
            <div style={{ fontSize: 12, color: "rgba(244,241,232,0.5)", fontStyle: "italic", marginBottom: 8 }}>
              Aucun avis pour le moment. Sois le premier à partager ton expérience.
            </div>
          )}
          {reviews.list.slice(0, 3).map((rv) => (
            <div key={rv.id} style={{
              padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8, marginBottom: 6,
              border: "1px solid rgba(255,255,255,0.05)",
            }}>
              <div style={{ fontSize: 11, color: "#FBBF24", marginBottom: 2 }}>{"★".repeat(rv.rating)}</div>
              <div style={{ fontSize: 12, color: "rgba(244,241,232,0.85)" }}>{rv.comment || "(sans commentaire)"}</div>
              <div style={{ fontSize: 10, color: "rgba(244,241,232,0.4)", marginTop: 2 }}>— {rv.author_pseudo}</div>
            </div>
          ))}
          {/* Form ajout */}
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`poi-rating-${n}`}
                  onClick={() => setReviewDraft((d) => ({ ...d, rating: n }))}
                  style={{
                    background: "transparent", border: "none", cursor: "pointer", fontSize: 18,
                    color: n <= reviewDraft.rating ? "#FBBF24" : "rgba(244,241,232,0.25)",
                  }}
                >★</button>
              ))}
            </div>
            <textarea
              placeholder="Ton avis (optionnel)"
              value={reviewDraft.comment}
              onChange={(e) => setReviewDraft((d) => ({ ...d, comment: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, resize: "vertical" }}
            />
            <button data-testid="poi-review-submit" onClick={onSubmitReview} disabled={submitting} style={btnPrimary}>
              {submitting ? "Envoi…" : "Publier mon avis"}
            </button>
          </div>
        </div>
      )}

      {/* ─── À proximité (stub MVP) ─── */}
      <div style={{
        padding: "12px 18px 20px", borderTop: "1px solid rgba(255,255,255,0.06)",
        fontSize: 11, color: "rgba(244,241,232,0.4)", fontStyle: "italic",
      }}>
        💡 « À proximité » arrive dans une prochaine version
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        background: "rgba(212,175,55,0.08)", color: "#F4F1E8",
        border: "1px solid rgba(212,175,55,0.2)", borderRadius: 10,
        padding: "8px 4px", cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
        fontSize: 10, fontWeight: 600,
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      {label}
    </button>
  );
}

function DetailLine({ icon, label, value }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "5px 0", fontSize: 12 }}>
      <span style={{ width: 18 }}>{icon}</span>
      <div style={{ color: "rgba(244,241,232,0.55)", width: 80 }}>{label}</div>
      <div style={{ flex: 1, color: "rgba(244,241,232,0.95)", wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}

const inputStyle = {
  background: "rgba(0,0,0,0.25)",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#F4F1E8",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 12,
  outline: "none",
};

const btnPrimary = {
  flex: 1, padding: "9px 12px", borderRadius: 8, border: "none",
  background: "linear-gradient(135deg, #D4AF37, #A07C20)", color: "#0F1A2E",
  fontWeight: 700, fontSize: 12, cursor: "pointer",
};

const btnGhost = {
  flex: 1, padding: "9px 12px", borderRadius: 8,
  background: "transparent", color: "rgba(244,241,232,0.7)",
  border: "1px solid rgba(255,255,255,0.15)", cursor: "pointer", fontSize: 12,
};

export { PIN_COLORS, PIN_ICONS };
